<template>
  <platform-toast-message />
  <div class="flex h-full w-full rounded-md" :class="{ 'max-h-140 max-w-220': config.isWidget }">
    <loading-fullscreen :show="isFullScreenLoading" />
    <platform-main>
      <component :is="currentScreenComponent" />
    </platform-main>

    <platform-controls v-if="config.isWidget" />
  </div>
  <popup-chat-container v-if="showPopupChat" />
  <div id="modals" />
  <div id="mobile-controls" class="fixed bottom-0 w-full" />
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref, onErrorCaptured } from "vue";
import config from "@/config";
import LoadingFullscreen from "@/components/loading/LoadingFullscreen.vue";
import PlatformControls from "@/components/platform/PlatformControls.vue";
import PlatformMain from "@/components/platform/PlatformMain.vue";
import PlatformToastMessage from "@/components/platform/PlatformToastMessage.vue";

import ApplicationWasUpdatedEvent from "@/api/ws/events/ApplicationWasUpdatedEvent";
import { useFullstory, FullstoryEvent } from "@/composables/fullstory";
import useApplicationsState from "@/composables/state/applicationsState";
import useAuthState from "@/composables/state/authState";
import useBrandingState from "@/composables/state/brandingState";
import { useChatState } from "@/composables/state/chatSessionState";
import useOptionsState from "@/composables/state/optionsState";
import usePlatformScreenState, { screenQueryToTab } from "@/composables/state/platformScreenState";
import { usePromiseWrapper } from "@/composables/common";
import useToastState from "@/composables/state/toastState";
import { useI18n } from "vue-i18n";
import { ApplicationPrivateChannel } from "@/api/ws/channels/ApplicationPrivateChannel";
import { BorrowerPrivateChannel } from "@/api/ws/channels/BorrowerPrivateChannel";
import BorrowerConnectedToProfileEvent from "@/api/ws/events/BorrowerConnectedToProfileEvent";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import PopupChatContainer from "@/components/chat/popup/PopupChatContainer.vue";
import { BorrowerPlatformTemplatesHttpService } from "@/api/http/BorrowerPlatformTemplatesHttpService";
import { TabType, WelcomePageType } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";

const {
  getActiveApplication,
  getApplications,
  getApplicationAndSetAsSelected,
  allApplications,
  selectedApplication,
  setSelectedApplication,
} = useApplicationsState();
const {
  activePlatformTemplate,
  currentScreenComponent,
  selectedTab,
  welcomeScreenTab,
  setSelectedTabByType,
  showLoadingFullscreen: httpServiceIsLoading,
} = usePlatformScreenState();
const { options, getOptions } = useOptionsState();
const { authToken, loggedInUser, getLoggedInUser, loginWithSecret, setAuthToken } = useAuthState();
const { hasSession } = useChatState();
const { init: initFullstory, publishEvent } = useFullstory();
const { isBrandingLoading, getBranding } = useBrandingState();
const toast = useToastState();
const { t } = useI18n();

const appIsInitalizing = ref(false);
const applicationPrivateChannel = ref<ApplicationPrivateChannel | null>(null);
const borrowerPrivateChannel = ref<BorrowerPrivateChannel | null>(null);

const bodyClassList = config.isWidget ? ["bg-black", "bg-opacity-60"] : ["bg-gray-100"];
bodyClassList.forEach((className) => {
  if (!document.body.classList.contains(className)) {
    document.body.classList.add(className);
  }
});

const showPopupChat = computed(() => {
  if (!hasSession.value) {
    return false;
  }

  if (selectedTab.value?.type === TabType.welcomePage) {
    return welcomeScreenTab.value?.mode !== WelcomePageType.AiAgent;
  }

  return true;
});

const isFullScreenLoading = computed(
  () => appIsInitalizing.value || httpServiceIsLoading.value || isBrandingLoading.value
);

const initApplications = async () => {
  await getApplications();
  const activeApplicationId = selectedApplication.value?.id;
  const firstApplicatonId = activeApplicationId || allApplications.value?.[0]?.id;
  if (firstApplicatonId) {
    await getApplicationAndSetAsSelected(firstApplicatonId);
  }
  await getActiveApplication();
};

const { executeFetch: getBorrowerPlatformTemplate } = usePromiseWrapper(async () => {
  const bpTemplateApi = new BorrowerPlatformTemplatesHttpService();

  const bpTemplateIdFromQuery = config.platformTemplateId;
  const applicationBpTemplateId = selectedApplication.value?.default_borrower_platform_template_id;
  const fallbackBpTemplateId = options.value?.fallback_borrower_platform_template_id;

  const templateIds = [bpTemplateIdFromQuery, applicationBpTemplateId, fallbackBpTemplateId].filter(
    (id): id is string => Boolean(id) && typeof id === "string"
  );

  for (const templateId of templateIds) {
    try {
      activePlatformTemplate.value = await bpTemplateApi.show(templateId);
      return;
    } catch (error) {
      // Try next template
    }
  }
});

watch(authToken, (newAuthToken, oldToken) => {
  getLoggedInUser();
  if (!newAuthToken || newAuthToken !== oldToken) {
    setSelectedApplication(null);

    if (!!config.page && screenQueryToTab[config.page]) {
      setSelectedTabByType(screenQueryToTab[config.page]);
      return;
    }

    setSelectedTabByType(TabType.workflow);
  }
});

watch(loggedInUser, async (newUserValue, previousUserValue) => {
  if (previousUserValue) {
    borrowerPrivateChannel.value?.leave();
  }
  if (!newUserValue) {
    return;
  }
  borrowerPrivateChannel.value = new BorrowerPrivateChannel(newUserValue.id);
  borrowerPrivateChannel.value.subscribeTo(new BorrowerConnectedToProfileEvent(initApplications));

  await initApplications();
});

watch(
  () => selectedApplication.value?.id,
  (newDealId, previousDealId) => {
    if (previousDealId) {
      applicationPrivateChannel.value?.leave();
    }
    if (newDealId) {
      applicationPrivateChannel.value = new ApplicationPrivateChannel(newDealId);
      applicationPrivateChannel.value.subscribeTo(
        new ApplicationWasUpdatedEvent((data) => {
          getApplicationAndSetAsSelected(data.id);
        })
      );
    }
  },
  { immediate: true }
);

watch(() => selectedApplication.value?.id, getBorrowerPlatformTemplate);

onErrorCaptured((error, component, info) => {
  toast.value.addMessage({
    title: t("common.errorOccurred"),
    type: "error",
    canClose: false,
    autoClose: false,
    action: {
      label: t("common.refresh"),
      handler: () => window.location.reload(),
    },
  });
  publishEvent(FullstoryEvent.UnandledErrorThrown, {
    componentName: component?.$options?.__name,
    error: error.message,
    info,
  });
});

onMounted(async () => {
  try {
    appIsInitalizing.value = true;

    await getOptions();
    await getBranding();

    initFullstory();

    if (config.isProduction && config.amplitudeApiKey) {
      amplitude.add(sessionReplayPlugin());
      amplitude.init(config.amplitudeApiKey, { fetchRemoteConfig: true, autocapture: true });
    }

    if (config.jwt) {
      setAuthToken(config.jwt);
    }

    if (config.email && config.secret && !authToken.value) {
      // https://drive.google.com/file/d/1NMs78wx9t-NlrYSiJppc1JIPOF78WK4p/view?usp=sharing
      await loginWithSecret();
    } else {
      await getLoggedInUser();
    }

    await getBorrowerPlatformTemplate();

    if (config.page === "documents") {
      setSelectedTabByType(TabType.documents);
    }
  } finally {
    appIsInitalizing.value = false;
  }
});
</script>

<style>
@reference "@/tailwind.css";

html,
body,
#app {
  @apply h-full w-full;
}

html {
  @apply text-gray-400;
}

#app {
  @apply flex h-full w-full items-center justify-center;
}

.tippy-box[data-theme~="lendflow"] {
  @apply text-xs leading-5 font-normal break-words text-white;
}

a {
  color: #3769d6;
}
</style>
